import React, { useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Provider, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import isEqual from 'lodash/isEqual';
import { FormProps } from '@omilia/chat-ui';
import root from 'react-shadow/styled-components';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import { makeTheme } from './styles/theme';
import {
  setToken,
  setChatConfig,
  setApiUrlDomain,
  updateChatConfig,
} from './store/services/session/actions';
import {
  getChatConfig,
  getIsPreviewMode,
} from './store/services/session/selectors';
import initializeStore from './store';
import Chat from './containers/Chat';
import { getChatMeta, getIsHistory } from 'store/services/chat/selectors';
import {
  closeChatBox,
  openChat,
  restartChat,
  setChatData,
} from './store/services/chat/actions';
import {
  Footer,
  Notifications,
  UrlSelectMethod,
  ChatResponder,
  Feedback,
  ReopenMinimiziedChat,
  ReopenMinimizedChatState,
} from 'types';
import { setCrossTabSync } from 'services/storage';
import { getChatContainer } from 'utils/getChatContainer';
import useCopyStyles from 'hooks/useCopyStyles';
import { LanguageProvider } from '@omilia/providers';
import { i18n } from 'utils/i18n';

const WebFont = require('webfontloader');

interface Config {
  title?: string;
  subtitle?: string;
  logoUrl?: string;
  logoAlt?: string;
  logoBorder?: boolean;
  bubbleUrl?: string;
  bubbleAlt?: string;
  brandColor?: string;
  backgroundColor?: string;
  apiUrl?: string;
  apiUrls?: string[];
  urlSelectMethod?: UrlSelectMethod;
  apiKey?: string;
  previewMode?: boolean;
  enableResetButton?: boolean;
  dataPersistent?: boolean;
  enableConsecutiveDialogs?: boolean;
  enableTranscriptDownload?: boolean;
  enableTransferDisableInput?: boolean;
  disableSecuredStyles?: boolean;
  shouldReopenMinimizedChat?: ReopenMinimiziedChat;
  defaultSize?: object;
  welcomeForm?: FormProps;
  ga?: boolean;
  font?: string;
  footer?: Footer;
  notifications?: Notifications;
  feedback?: Feedback;
  agent?: ChatResponder;
  bot?: ChatResponder;
  crossTabSync?: boolean;
  locale?: string;
  enableSensitiveInputMask?: boolean;
}

interface Props {
  data: Record<string, any>;
  config: Config;
}

interface Configuration {
  rootElementId: string;
  config: Config;
  data: Record<string, any>;
}

type SubscribeEvent = 'onOpen' | 'onMinimized' | 'onRestart';

let store;

const DEFAULT_FONT = 'Montserrat, sans-serif';

function ThemedChat() {
  const config = useSelector(getChatConfig);
  const isPreviewMode = useSelector(getIsPreviewMode);
  const liveFont = config.font;
  const initialFontRef = useRef(liveFont);

  const theme = useMemo(
    () =>
      makeTheme({
        logoUrl: config.logoUrl,
        logoAlt: config.logoAlt,
        logoBorder: config.logoBorder,
        bubbleUrl: config.bubbleUrl,
        bubbleAlt: config.bubbleAlt,
        primaryColor: config.brandColor,
        title: config.title,
        fontFamily: config.font,
      }),
    [
      config.logoUrl,
      config.logoAlt,
      config.logoBorder,
      config.bubbleUrl,
      config.bubbleAlt,
      config.brandColor,
      config.title,
      config.font,
    ],
  );

  // Preview mode: re-load the Google Font whenever the host updates it via
  // updateConfig. Production stays on the mount-only effect below.
  useEffect(() => {
    if (!isPreviewMode) return;
    if (!liveFont || liveFont === DEFAULT_FONT) return;
    WebFont.load({ google: { families: [liveFont] } });
  }, [liveFont, isPreviewMode]);

  useEffect(() => {
    if (isPreviewMode) return;
    const initialFont = initialFontRef.current;
    if (initialFont && initialFont !== DEFAULT_FONT) {
      WebFont.load({ google: { families: [initialFont] } });
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Chat />
    </ThemeProvider>
  );
}

export function ChatApp({
  data,
  config: {
    title,
    subtitle,
    logoUrl,
    logoAlt,
    logoBorder,
    bubbleUrl,
    bubbleAlt,
    brandColor,
    backgroundColor,
    apiUrl,
    apiUrls,
    urlSelectMethod,
    apiKey,
    previewMode = false,
    welcomeForm,
    enableResetButton = false,
    enableConsecutiveDialogs = true,
    enableTranscriptDownload = false,
    enableTransferDisableInput = true,
    disableSecuredStyles = false,
    shouldReopenMinimizedChat = ReopenMinimizedChatState.NEXT_TABS,
    dataPersistent = false,
    ga = false,
    font = DEFAULT_FONT,
    footer,
    notifications,
    feedback,
    agent,
    bot,
    crossTabSync = true,
    locale = 'en',
    enableSensitiveInputMask = false,
  },
}: Props) {
  const cache = createCache({
    container: getChatContainer(disableSecuredStyles),
    key: 'omilia-emotion',
  });

  const getFontFamily = () => font.split(':')[0];

  const config = {
    enableResetButton,
    dataPersistent,
    enableConsecutiveDialogs,
    enableTranscriptDownload,
    enableTransferDisableInput,
    disableSecuredStyles,
    shouldReopenMinimizedChat,
    title,
    subtitle,
    welcomeForm,
    name,
    font: getFontFamily(),
    ga,
    logoUrl,
    logoAlt,
    logoBorder,
    bubbleUrl,
    bubbleAlt,
    brandColor,
    backgroundColor,
    footer,
    notifications,
    feedback,
    agent,
    bot,
    locale,
    previewMode,
    enableSensitiveInputMask,
  };
  store = initializeStore({
    apiUrl,
    apiUrls,
    urlSelectMethod,
    previewMode,
  });

  useCopyStyles({ disableSecuredStyles });

  useEffect(() => {
    if (locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale]);

  const currentApiURL = apiUrls?.length ? apiUrls[0] : apiUrl;

  // setChatConfig(config) needs to be dispatched firstly
  // because the way data is stored (persistent or session-only) it depends on the config
  store.dispatch(setChatConfig(config));
  store.dispatch(setApiUrlDomain(currentApiURL));
  store.dispatch(setToken(apiKey));
  store.dispatch(setChatData(data));
  setCrossTabSync(crossTabSync);
  return (
    <CacheProvider value={cache}>
      {/* @ts-ignore */}
      <LanguageProvider i18n={i18n}>
        <Provider store={store}>
          <ThemedChat />
        </Provider>
      </LanguageProvider>
    </CacheProvider>
  );
}

export function init({ rootElementId, data, config }: Configuration) {
  if (!config?.previewMode && (!config?.apiUrl || !config?.apiKey)) {
    throw new Error(
      'webchat: apiUrl and apiKey are required unless previewMode is enabled.',
    );
  }
  return ReactDOM.render(
    config?.disableSecuredStyles ? (
      <div className="chat-container">
        <ChatApp data={data} config={config} />
      </div>
    ) : (
      <root.div className="shadow-dom">
        <ChatApp data={data} config={config} />
      </root.div>
    ),
    document.getElementById(rootElementId),
  );
}

export const open = utterance => store.dispatch(openChat({ utterance }));

export const close = () => store.dispatch(closeChatBox());

export const restart = () => store.dispatch(restartChat());

export const setData = data => store.dispatch(setChatData(data));

export const updateConfig = (partial: Partial<Config>) =>
  store.dispatch(updateChatConfig(partial));

export const subscribe = (event: SubscribeEvent, callback) => {
  let currentState = {
    isOpen: false,
    isMinimized: false,
    isRestarted: false,
  };

  store.subscribe(() => {
    const { chat } = store.getState();
    const prevState = {
      isOpen: currentState.isOpen,
      isMinimized: currentState.isMinimized,
      isRestarted: currentState.isRestarted,
    };

    currentState = {
      isOpen: chat.isOpen,
      isMinimized: chat.isMinimized,
      isRestarted: chat.isRestarted,
    };

    if (event === 'onOpen' && !isEqual(prevState.isOpen, currentState.isOpen)) {
      callback(currentState.isOpen);
    }

    if (
      event === 'onMinimized' &&
      !isEqual(prevState.isMinimized, currentState.isMinimized)
    ) {
      callback(currentState.isMinimized);
    }

    if (
      event === 'onRestart' &&
      !isEqual(prevState.isRestarted, currentState.isRestarted) &&
      currentState.isRestarted
    ) {
      callback(currentState.isRestarted);
    }
  });
};

export const onDebugEvents = callback => {
  let currentState = {
    debugInfo: null,
  };

  store.subscribe(() => {
    const { debugInfo } = store.getState().chat;

    const prevState = {
      debugInfo: currentState.debugInfo,
    };

    currentState = {
      debugInfo,
    };

    if (
      currentState.debugInfo &&
      !isEqual(prevState.debugInfo, currentState.debugInfo)
    ) {
      callback(currentState.debugInfo);
    }
  });
};

export const setEventCallback = callback => {
  let currentMeta = null;

  store.subscribe(() => {
    const meta = getChatMeta(store.getState());
    const isHistory = getIsHistory(store.getState());

    const prevMeta = currentMeta;
    currentMeta = meta;

    if (
      !document.hidden &&
      !isHistory &&
      currentMeta &&
      !isEqual(prevMeta, currentMeta)
    ) {
      callback(currentMeta);
    }
  });
};

export default init;
